import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Box,
  Typography,
  IconButton,
  Badge,
  Chip,
  Switch,
  Collapse,
  Button
} from '@mui/material';
import {
  AccountCircle,
  CreditCard,
  Settings,
  Help,
  Logout,
  Notifications,
  Security,
  Storage,
  Analytics,
  Close,
  ExpandLess,
  ExpandMore,
  Person,
  Speed,
  DarkMode,
  Language
} from '@mui/icons-material';

const UserDrawer = ({ open, onClose, user, onLogout }) => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    account: false,
    settings: false
  });

  const handleSectionToggle = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const menuItems = [
    {
      id: 'account',
      title: 'Account',
      icon: <AccountCircle />,
      expandable: true,
      subItems: [
        { id: 'profile', title: 'Profile Settings', icon: <Person /> },
        { id: 'security', title: 'Security', icon: <Security /> },
        { id: 'notifications', title: 'Notifications', icon: <Notifications /> }
      ]
    },
    {
      id: 'billing',
      title: 'Billing & Subscription',
      icon: <CreditCard />,
      badge: 'Pro',
      onClick: () => alert('Billing page would open here')
    },
    {
      id: 'storage',
      title: 'Storage & Usage',
      icon: <Storage />,
      onClick: () => alert('Storage analytics would open here')
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: <Settings />,
      expandable: true,
      subItems: [
        { id: 'preferences', title: 'Preferences', icon: <Speed /> },
        { id: 'display', title: 'Display', icon: <DarkMode /> },
        { id: 'language', title: 'Language', icon: <Language /> }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics',
      icon: <Analytics />,
      onClick: () => alert('Analytics dashboard would open here')
    },
    {
      id: 'help',
      title: 'Help & Support',
      icon: <Help />,
      onClick: () => alert('Help center would open here')
    }
  ];

  const storageUsed = 45; // Percentage

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 320,
          boxSizing: 'border-box',
          background: '#242424',
          color: '#e0e0e0'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)', color: 'white', borderBottom: '1px solid #333' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">User Menu</Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </Box>
        
        {/* User Info */}
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: '#4285f4',
              color: 'white',
              fontSize: '1.5rem',
              fontWeight: 'bold'
            }}
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {user?.name || 'User'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {user?.email || 'user@example.com'}
            </Typography>
            <Chip
              label={user?.role || 'Physician'}
              size="small"
              sx={{
                mt: 0.5,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '0.75rem'
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Box sx={{ p: 2, bgcolor: '#1a1a1a', borderBottom: '1px solid #333' }}>
        <Typography variant="body2" color="#aaa" gutterBottom>
          Storage Usage
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ flexGrow: 1, height: 8, bgcolor: '#333', borderRadius: 4, overflow: 'hidden' }}>
            <Box
              sx={{
                width: `${storageUsed}%`,
                height: '100%',
                bgcolor: storageUsed > 80 ? '#f44336' : '#4285f4',
                transition: 'width 0.3s ease'
              }}
            />
          </Box>
          <Typography variant="caption" color="#aaa">
            {storageUsed}%
          </Typography>
        </Box>
        <Typography variant="caption" color="#888" sx={{ mt: 0.5, display: 'block' }}>
          4.5 GB of 10 GB used
        </Typography>
      </Box>

      <Divider sx={{ borderColor: '#333' }}/>

      {/* Menu Items */}
      <List sx={{ flexGrow: 1, bgcolor: '#242424' }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.id}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={item.expandable ? () => handleSectionToggle(item.id) : item.onClick}
                sx={{
                  '&:hover': {
                    bgcolor: '#333'
                  }
                }}
              >
                <ListItemIcon sx={{ color: '#e0e0e0' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.title} sx={{ color: '#e0e0e0' }} />
                {item.badge && (
                  <Chip label={item.badge} size="small" color="primary" />
                )}
                {item.expandable && (
                  expandedSections[item.id] ? <ExpandLess /> : <ExpandMore />
                )}
              </ListItemButton>
            </ListItem>
            
            {item.expandable && (
              <Collapse in={expandedSections[item.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItemButton
                      key={subItem.id}
                      sx={{ 
                        pl: 4,
                        '&:hover': {
                          bgcolor: '#333'
                        }
                      }}
                      onClick={() => alert(`${subItem.title} would open here`)}
                    >
                      <ListItemIcon sx={{ color: '#aaa' }}>{subItem.icon}</ListItemIcon>
                      <ListItemText primary={subItem.title} sx={{ color: '#aaa' }} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
        
        {/* Notifications Toggle */}
        <ListItem sx={{ bgcolor: '#242424' }}>
          <ListItemIcon sx={{ color: '#e0e0e0' }}>
            <Badge badgeContent={3} color="error">
              <Notifications />
            </Badge>
          </ListItemIcon>
          <ListItemText primary="Notifications" sx={{ color: '#e0e0e0' }} />
          <Switch
            edge="end"
            checked={notificationsEnabled}
            onChange={(e) => setNotificationsEnabled(e.target.checked)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#4285f4',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#4285f4',
              },
            }}
          />
        </ListItem>
        
        {/* Dark Mode Toggle */}
        <ListItem sx={{ bgcolor: '#242424' }}>
          <ListItemIcon sx={{ color: '#e0e0e0' }}>
            <DarkMode />
          </ListItemIcon>
          <ListItemText primary="Dark Mode" sx={{ color: '#e0e0e0' }} />
          <Switch
            edge="end"
            checked={darkMode}
            onChange={(e) => setDarkMode(e.target.checked)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#4285f4',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#4285f4',
              },
            }}
          />
        </ListItem>
      </List>

      <Divider sx={{ borderColor: '#333' }}/>

      {/* Footer Actions */}
      <Box sx={{ p: 2, bgcolor: '#1a1a1a' }}>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<Logout />}
          onClick={onLogout}
          sx={{ 
            mb: 1,
            borderColor: '#f44336',
            color: '#f44336',
            '&:hover': {
              borderColor: '#d32f2f',
              backgroundColor: 'rgba(244, 67, 54, 0.08)'
            }
          }}
        >
          Sign Out
        </Button>
        <Typography variant="caption" color="#666" align="center" display="block">
          Version 1.0.0 • Last login: Today at 10:30 AM
        </Typography>
      </Box>
    </Drawer>
  );
};

export default UserDrawer;